<template>
	<ul v-if="questions" 
		:class="[{
			loading: loading,
		}]">
		<li 
			v-for="question in questions" :class="['accordion-item', {
				loading: loading,
				active : (activeQuestionId===question.id)
			}]" 
			:id="'question-' + question.id" 
			:aria-controls="'answer-' + question.id"
			:aria-expanded="(activeQuestionId===question.id) ? false : true">
			<a 
				:href="'#' + question.slug" 
				class="accordion-title" 
				@click.stop.prevent="getQuestionResponse(question.id)">
				<span class="unique-id">ID: {{ question.id }}</span>
				<span class="category">{{ question.categories }}</span>
				<span v-if="question.avsnitt" class="avsnitt">{{ question.avsnitt }}</span>
				
				<br />
				<span v-if="question.subject" class="subject">{{ question.subject }}</span>
				<span :class="['dc-icon-circle', {
					'icon-minus' : (activeQuestionId===question.id),
					'icon-plus' : (activeQuestionId!==question.id),
					'loading' : (activeQuestionId===question.id && loading)
				}]">
					<span v-if="activeQuestionId===question.id && loading" class="dot"></span>
					<span v-if="activeQuestionId===question.id && loading" class="dot"></span>
					<span v-if="activeQuestionId===question.id && loading" class="dot"></span>
					<span class="screen-reader-text">Les mer</span>
				</span>
			</a>

			<div 
				:class="['accordion-content']" 
				role="tabpanel" 
				:aria-controlledby="'question-' + question.id"
				:aria-hidden="(activeQuestionId===question.id) ? false : true" 
				:aria-busy="loading" 
				aria-live="polite">
				<article 
					v-if="typeof responses[question.id] !== 'undefined' && (activeQuestionId===question.id)" 
					class="faq-item">
					<div class="date-column">
						<dl>
							<dt class="show-for-search-only">Avsnitt</dt>
							<dd class="show-for-search-only">{{ responses[question.id].avsnitt }}</dd>
							<dt class="show-for-search-only">ID:</dt>
							<dd class="show-for-search-only">{{ responses[question.id].id }}</dd>
							<dt class="date"><i class="icon-lastedited"></i> Dato</dt>
							<dd class="date"><time :data-timestamp="responses[question.id].timestamp">{{ responses[question.id].date }}</time></dd>
						</dl>
					</div>
					<div class="response-column">
						<h4>{{ responses[question.id].subject }}</h4>
						<div v-if="responses[question.id].has_multiple_variants">
							<p>Dette svaret har flere varianter. Velg år nedenfor.</p>
							<div class="field-wrapper select">
								<label for="filter-variant">Velg år:</label>
								<select v-model="model.selectedVariant">
									<option value="">Velg</option>
									<option v-for="variant in responses[question.id].variants" :value="variant.slug">{{ variant.label }}</option>
								</select>
							</div>
							<div v-for="variant in responses[question.id].variants" v-if="variant.slug===model.selectedVariant">
								<h5><i class="icon-question"></i> Spørsmål</h5>
								<div v-html="sanitizeText(variant.question)"></div>
								<h5><i class="icon-checked"></i> Svar</h5>
								<div v-html="sanitizeText(variant.answer)"></div>
							</div>
						</div>
						<div v-else>
							<h5><i class="icon-question"></i> Spørsmål</h5>
							<div v-html="sanitizedQuestion"></div>
							<h5><i class="icon-checked"></i> Svar</h5>
							<div v-html="sanitizedResponse"></div>
						</div>
					</div>
				</article>
			</div>
		</li>
	</ul>
</template>

<script>
import { get } from '../../../../../themes/nek-old/src/js/vendors/js.cookie';

export default {
	props: {
		loading: {
			type: Boolean,
			required: true,
		},
		questions: {
			type: Array,
			required: true,
		},
		endpoint: {
			type: Array,
			required: true,
		},
		preselectedVariant: {
			type: String,
			required: false,
			default: '',
		},
	},
	data() {
		return {
			activeQuestionId: null,
			activeQuestionResponse: null,
			responses: [],
			model: {
				selectedVariant: '',
			},
		}
	},
	mounted() {
		this.model.selectedVariant = this.preselectedVariant;
	},
	computed: {
		sanitizedResponse() {
			if (this.activeQuestionId===null) return '';
			if (typeof this.responses[this.activeQuestionId] === 'undefined') return '';
			return this.$sanitize(this.responses[this.activeQuestionId].answer);
		},
		sanitizedQuestion() {
			if (this.activeQuestionId===null) return '';
			if (typeof this.responses[this.activeQuestionId] === 'undefined') return '';
			return this.$sanitize(this.responses[this.activeQuestionId].question);
		},
	},
	methods: {
		getQuestionResponse(questionId) {
			this.activeQuestionId = (this.activeQuestionId===questionId) ? null : questionId;
		},
		lookupQuestionResponse(questionId) {
			if (typeof this.responses[questionId] !== 'undefined') return;
			this.loading = true;
			const endpointUrl = this.endpoint + questionId;
			fetch(endpointUrl)
			.then(response => response.json())
			.then(data => {
				this.loading = false;
				this.responses[questionId] = data.data.question;
				// check if current variant is in the list of variants, if not reset model to default value
				if (typeof this.responses[questionId] !== 'undefined') {
					if (this.responses[questionId].has_multiple_variants) {
						if (this.responses[questionId].variants.length>0) {
							if (this.responses[questionId].variants.filter(variant => variant.slug===this.model.selectedVariant).length===0) {
								this.model.selectedVariant = ''
							}
						}
					}
				}
			});
		},
		sanitizeText(text) {
			return this.$sanitize(text);
		}
	},
	watch: {
		questions(newVal) {
			if (newVal===[]) {
				// Clear cached responses
				this.responses = []
			}
		},
		selectedVariant(newVal) {
			this.$emit('update:currentCategoryVariant', newVal);
		},
		activeQuestionId(questionId) {
			if (questionId===null) {
				this.activeQuestionResponse = null;
			} else {
				if (typeof this.responses[questionId] === 'undefined') {
					this.lookupQuestionResponse(questionId)
				} else {
					this.activeQuestionResponse = this.responses[questionId];
				}
			}
			// check if current variant is in the list of variants, if not reset model to default value
			if (typeof this.responses[questionId] !== 'undefined') {
					if (this.responses[questionId].has_multiple_variants) {
						if (this.responses[questionId].variants.length>0) {
							if (this.responses[questionId].variants.filter(variant => variant.slug===this.model.selectedVariant).length===0) {
								this.model.selectedVariant = ''
							}
						}
					}
				}
		},
		getQuestionResponse(questionId) {
			this.activeQuestionId = (this.activeQuestionId===questionId) ? null : questionId;
		},
		lookupQuestionResponse(questionId) {
			if (typeof this.responses[questionId] !== 'undefined') return;
			this.loading = true;
			const endpointUrl = this.settings.question_endpoint + questionId;
			fetch(endpointUrl)
			.then(response => response.json())
			.then(data => {
				this.loading = false;
				this.responses[questionId] = data.data.question;
			});
		},
	}
};
</script>
